// COLUMN WIDTHS — the maths and the stored shape behind DataTable's
// drag-to-resize handles.
//
// ── WHY THIS IS ITS OWN FILE ────────────────────────────────────────────────
// Every rule here is a pure function of numbers and plain objects: clamp a
// pixel value, add a drag delta to a start width, decide whether a stored blob
// is trustworthy. None of it needs React, the DOM, or a browser — and the one
// thing that MUST be right (a corrupt or hostile localStorage blob never
// reaching a style attribute) is exactly the thing that is impossible to prove
// from inside a component. So the rules live here and scripts/test-columnWidths.mjs
// runs them under plain node.
//
// ── WHY WIDTHS ARE PERSISTED AT ALL ─────────────────────────────────────────
// The Activity detail tables are read as evidence: a member's follow-ups, with
// the subject line and the snippet, shown to someone who is checking a number.
// Under the browser's auto table layout the Subject column is whatever is left
// after six nowrap date/status columns have taken what they want, which on a
// laptop is a sliver. Dragging it wider has to survive the reload, or the user
// re-does it every single time they open the page.
//
// ── WHY A WIDTH IS ONLY EVER A PLAIN NUMBER OF PIXELS ───────────────────────
// The stored value goes straight into an inline `style.width`. A string from
// localStorage would go in just as happily — including `100px; background:url(…)`
// on an older browser, or a `calc()` that resolves to nothing and collapses the
// column to zero with no way back except clearing site data. So the parse below
// accepts a finite number in range and NOTHING else: no strings, no percentages,
// no NaN, no Infinity, no negative, no 1e9. Anything else is dropped, silently
// and per-column, which lands the table back on the browser's own layout — the
// same place a fresh table starts, i.e. fail-soft rather than a broken page.

// The floor is "still recognisably a column": a header caret plus two or three
// characters. Below ~64px a resize reads as a bug, and a mis-aimed drag that
// slams a column to 8px is very hard to recover from by eye.
//
// This is the ABSOLUTE floor — the narrowest any column may be stored at. It is
// only actually reachable by a column whose cells carry little padding; see
// minColumnWidth below for why a wider-padded column has a higher floor of its
// own, and why quoting 64 for one would be a lie.
export const MIN_COLUMN_WIDTH = 64
// How much room a column needs INSIDE its padding to still be a column rather
// than two rules with nothing between them: a couple of characters or an
// ellipsis. Small on purpose — this is the floor of the floor, not a comfortable
// width.
export const MIN_COLUMN_CONTENT = 16

// The floor for a column with `padding` pixels of horizontal cell padding.
//
// WHY THIS EXISTS. `* { box-sizing: border-box }` is global (src/styles.css), so
// a cell's padding is INSIDE its width: a column asked for 64px whose cells pad
// 96px does not become 64px wide, it becomes 96px wide with a zero-width content
// box. Everything downstream then disagrees with the screen — the stored number
// says 64, aria-valuenow announces 64, tableWidthFor sums 64, and the browser
// lays out 96. Clamping to padding + MIN_COLUMN_CONTENT instead means the number
// we store is the number the browser uses, which is the whole point of storing it.
//
// Never below MIN_COLUMN_WIDTH: a barely-padded column still has to stay
// recognisable, and a column that could legally be 48px wide is a mis-drag away
// from looking broken.
export function minColumnWidth(padding = 0) {
  const pad = typeof padding === 'number' && Number.isFinite(padding) && padding > 0 ? padding : 0
  return Math.max(MIN_COLUMN_WIDTH, Math.round(pad + MIN_COLUMN_CONTENT))
}
// The ceiling is "still inside one horizontal scroll": 800px is wider than any
// real Subject line needs and stops a runaway drag (pointer leaves the window,
// browser reports a huge clientX) from producing a 12,000px table.
export const MAX_COLUMN_WIDTH = 800

// Keyboard resize steps. 16px is a visible nudge without being a jump; Shift
// gives ~3 of them so crossing the full range does not take 46 key presses.
export const COLUMN_WIDTH_STEP = 16
export const COLUMN_WIDTH_STEP_LARGE = 48

// Round to whole pixels. Sub-pixel widths come out of getBoundingClientRect on
// zoomed/fractional-DPI displays; storing 183.33333333333334 is noise that also
// makes the JSON bigger every time it round-trips.
function px(value) {
  return Math.round(value)
}

// A width is usable only if it is a real, finite, positive number. Written as
// an explicit typeof check rather than Number(v) because Number('  180  ') is
// 180 and Number(true) is 1 — a stored string or boolean is corruption, and
// coercing it would hide the corruption instead of dropping it.
export function isUsableWidth(value) {
  return typeof value === 'number' && Number.isFinite(value) && value > 0
}

function clampToRange(value, min, max) {
  return px(Math.min(Math.max(value, min), max))
}

// Clamp a CANDIDATE STORED WIDTH into [MIN, MAX]. Returns null — never a
// fallback width — for anything that is not already a usable number, so callers
// can tell "no width" (use the browser's layout) apart from "a width, clamped".
//
// Note the asymmetry with resizedWidth below, which is deliberate. A stored
// -12 is corruption and is dropped; a drag that arrives at -12 is a user
// pulling the handle off the left edge of the screen and lands on the floor.
// Same number, different meaning, because one has a start width behind it and
// the other does not.
export function clampColumnWidth(value, min = MIN_COLUMN_WIDTH, max = MAX_COLUMN_WIDTH) {
  if (!isUsableWidth(value)) return null
  return clampToRange(value, min, max)
}

// The drag itself: where the column started plus how far the pointer has moved.
// Deliberately absolute (start + total delta) rather than incremental
// (current + per-event delta) — an incremental sum drifts once the clamp bites,
// so a drag that pushes past the 800px ceiling and comes back would leave the
// column somewhere other than where the pointer is.
export function resizedWidth(startWidth, deltaX, min = MIN_COLUMN_WIDTH, max = MAX_COLUMN_WIDTH) {
  if (!isUsableWidth(startWidth)) return null
  // A non-finite delta means the pointer position could not be read; treat it
  // as no movement rather than as a collapsed column.
  const delta = Number.isFinite(deltaX) ? deltaX : 0
  return clampToRange(startWidth + delta, min, max)
}

// Read a stored widths blob into a map we are willing to render.
//
// Every entry is checked independently: one poisoned column loses its width,
// the rest of the user's layout survives. Keys are kept as-is (they are column
// keys the table itself declared) but only string keys are accepted, and the
// whole thing is rejected outright if it is not a plain object — an array or a
// string would otherwise enumerate into indices and produce a width for a
// column named "0".
export function sanitizeColumnWidths(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {}
  const out = {}
  for (const key of Object.keys(raw)) {
    if (!key) continue
    const w = clampColumnWidth(raw[key])
    if (w != null) out[key] = w
  }
  return out
}

export function hasAnyColumnWidth(widths) {
  return !!widths && Object.keys(widths).length > 0
}

// Keep only the widths belonging to columns the table still declares.
//
// A renamed or deleted column would otherwise leave its width in the prefs blob
// for good, and that orphan is not harmless: it makes the table look "resized"
// forever, which pins it to fixed layout and shows a Reset button for a column
// nobody can see. Pruning on load is self-healing — the next write drops it.
//
// `mins` is an optional { key: minWidth } from minColumnWidth(), so a width that
// predates a column's padding (or was hand-edited into localStorage below what
// the box model can honour) is raised to that column's own floor on the way in.
// Without it the loaded number and the laid-out column disagree from the first
// paint, and nothing later in the session ever brings them back together.
export function pickColumnWidths(widths, keys, mins) {
  const clean = sanitizeColumnWidths(widths)
  const allowed = new Set(keys || [])
  const out = {}
  for (const key of Object.keys(clean)) {
    if (!allowed.has(key)) continue
    const min = mins && isUsableWidth(mins[key]) ? mins[key] : MIN_COLUMN_WIDTH
    const w = clampColumnWidth(clean[key], min)
    if (w != null) out[key] = w
  }
  return out
}

// Set one column's width. Returns a NEW object (React state) and treats an
// unusable width as a reset rather than storing a broken value. `min` is the
// column's own padding-aware floor (minColumnWidth) — pass it, or a narrow drag
// stores a number the browser will not honour.
export function setColumnWidth(widths, key, width, min = MIN_COLUMN_WIDTH) {
  const w = clampColumnWidth(width, min)
  if (w == null) return clearColumnWidth(widths, key)
  return { ...sanitizeColumnWidths(widths), [key]: w }
}

// Back to the browser's own layout for ONE column (double-click the handle).
export function clearColumnWidth(widths, key) {
  const next = sanitizeColumnWidths(widths)
  delete next[key]
  return next
}

// Back to the browser's own layout for the whole table ("Reset column widths").
export function clearColumnWidths() {
  return {}
}

// Freeze the columns the user has NOT touched at whatever the browser had
// already given them, using measured header widths.
//
// This is the difference between a resize that feels deliberate and one that
// feels like the table exploded. Switching a table from `auto` to `fixed`
// layout re-computes every column from scratch, so if only the dragged column
// carried a width, the other six would all jump the instant the drag began.
// Seeding them from their current on-screen widths makes the first frame of the
// drag pixel-identical to the last frame before it.
//
// `measured` is [{ key, width, min }] in display order; entries without a usable
// measurement are skipped (a column can measure 0 while the table is hidden in
// a collapsed panel, and a 0 would collapse it for good). Widths the user has
// already set win — a measurement is only ever a starting point.
//
// `min` is the column's padding-aware floor. A real measurement is never below
// its own padding, so this practically never bites; when it does (a column whose
// header is a single glyph) a 16px nudge at the start of the drag is the lesser
// evil against seeding a number the box model will silently overrule.
export function seedColumnWidths(widths, measured) {
  const next = sanitizeColumnWidths(widths)
  for (const item of measured || []) {
    if (!item || !item.key || next[item.key] != null) continue
    const w = clampColumnWidth(item.width, isUsableWidth(item.min) ? item.min : MIN_COLUMN_WIDTH)
    if (w != null) next[item.key] = w
  }
  return next
}

// The explicit table width to render, or null to leave the table alone.
//
// Under `table-layout: fixed` the browser honours the per-column widths only
// while their sum agrees with the table's own width; if the table is wider it
// hands the slack back out proportionally and the column the user just dragged
// quietly grows again. So once every visible column has a width we state the
// total, and the wrapper's existing overflow-auto turns the overflow into the
// horizontal scroll the user asked for by dragging.
//
// Null when ANY visible column is still auto (a column shipped after the user
// last resized, or one they double-clicked back to auto): that column is the
// one meant to absorb the slack, and pinning a total would fight it.
export function tableWidthFor(widths, keys, extra = 0) {
  if (!keys || !keys.length) return null
  let total = extra
  for (const key of keys) {
    const w = widths ? widths[key] : null
    if (!isUsableWidth(w)) return null
    total += w
  }
  return px(total)
}

// ── PREFS ───────────────────────────────────────────────────────────────────
// Widths ride in the SAME per-user, per-table prefs blob as the column picker's
// visible set and rows-per-page. One store, one key, one write: a second store
// would need its own scoping, its own migration and its own clean-up on sign-out,
// and would drift out of step with the visible-column list it depends on.

// Merge widths into an existing prefs blob WITHOUT inventing the fields we do
// not own. In particular this must never write a `visibleKeys` the user never
// chose: DataTable treats a saved visibleKeys as "the user has an opinion" and
// stops auto-surfacing newly shipped columns into it. Resizing a column is not
// an opinion about which columns exist.
export function writeWidthsToPrefs(prefs, widths) {
  const base = prefs && typeof prefs === 'object' && !Array.isArray(prefs) ? prefs : {}
  const clean = sanitizeColumnWidths(widths)
  const next = { ...base }
  // An empty map is stored as an absent field, not as `{}`, so "reset" leaves
  // the prefs blob byte-identical to one that was never resized.
  if (hasAnyColumnWidth(clean)) next.colWidths = clean
  else delete next.colWidths
  return next
}

export function readWidthsFromPrefs(prefs) {
  if (!prefs || typeof prefs !== 'object' || Array.isArray(prefs)) return {}
  return sanitizeColumnWidths(prefs.colWidths)
}

// ── THE STORAGE KEY ─────────────────────────────────────────────────────────
// Lives here (rather than in the component) so the per-table isolation is
// testable: the Activity detail overlays are all the same React element with a
// different `tableId`, so "followups" and "replies" keeping separate widths is
// a property of this function and nothing else.

function hashId(str) {
  let h = 0
  for (let i = 0; i < str.length; i += 1) h = (Math.imul(h, 31) + str.charCodeAt(i)) | 0
  return (h >>> 0).toString(36)
}

// The unscoped base key. The caller runs it through scopedKey() so two accounts
// on one browser never share a layout. Tables without an explicit id fall back
// to a hash of their column keys, which is stable for a given table and
// different between tables — good enough that no caller has to wire an id just
// to get its own prefs.
export function tablePrefsBaseKey(tableId, columnKeys) {
  const id = tableId || `auto_${hashId((columnKeys || []).join('|'))}`
  return `revailosales.tcols.${id}`
}
